<!-- Reusable template for the main content area -->
<ng-template #mainContentTpl>
  <div class="main-content-area">
    <div class="mb-2 mt-4 flex items-center gap-3 md:mb-4 md:mt-0 md:block">
      <div
        class="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full md:mb-4 md:h-[56px] md:w-[56px]"
        [ngClass]="['bg-dynamic-' + state.topic!.topicLevel1?.color_name + '-400']"
        [class.mt-8]="!state.isMobile"
      >
        <dojo-icon
          [icon]="state.topic!.icon!"
          [size]="state.isMobile ? 16 : 30"
          [alt]="state.topic!.code"
          [ngClass]="['text-dynamic-' + state.topic!.topicLevel1?.color_name + '-50']"
          [faPrefix]="'fas'"
        />
      </div>
      <div class="text-primary text-2xl font-bold">
        @if (!state.isMobile) {
          {{ state.topic!.code }}
        }
        <dojo-localized-string [content]="state.topic!.name" />
      </div>
    </div>
    <div class="text-primary text-md mb-6">
      @if (state.topic!.description | localize | async; as content) {
        <dojo-markdown-viewer [content]="content" />
      }
    </div>
    <div class="w-full" style="border-bottom: 1px solid var(--neutral-200)"></div>
    <div class="py-6">
      @if (state.activePage && !state.showEmptyState) {
        <div class="h-full">
          @if (state.commentsEnabled && state.collabConfig) {
            <dojo-study-notes-editor
              [content]="state.activePage.content"
              [editable]="false"
              [showTableOfContents]="false"
              [collabConfig]="state.collabConfig"
              [formulaBookletUrl]="state.formulaBookletUrl ?? ''"
              contentPadding="0"
            />
          } @else {
            <dojo-content-blocks-renderer
              [blocks]="state.activePage.content"
              [defaultFormulaBookletUrl]="state.formulaBookletUrl ?? ''"
            />
          }
          <dojo-related-articles [slugs]="state.activePage.related_pages" />
        </div>
      } @else if (state.showEmptyState) {
        <div class="flex flex-col items-center pt-8 text-center">
          <img src="assets/images/sensei/sensei-encouraging.svg" alt="Sensei" class="mb-4 h-[168px]" />
          <h3 class="text-primary text-2xl font-bold">{{ 'practice.notes_coming_soon' | transloco }}</h3>
          <p class="text-secondary text-md mt-2 max-w-sm">
            {{ 'practice.notes_coming_soon_subtitle' | transloco }}
          </p>
        </div>
        <div class="mb-4 mt-8 flex flex-col">
          <dojo-card-link
            [routerLink]="`/${RootUrlParts.TopicPractice}`"
            [title]="'home_card.card_1.title' | transloco"
            [subtitle]="'home_card.card_1.subtitle' | transloco"
            [imageSrc]="
              state.darkMode
                ? 'assets/images/topic-practice/roundel-dark.svg'
                : 'assets/images/topic-practice/roundel.svg'
            "
            class="mb-5"
          ></dojo-card-link>

          <dojo-card-link
            [routerLink]="`/${RootUrlParts.TopicPractice}`"
            [title]="'home_card.card_2.title' | transloco"
            [subtitle]="'home_card.card_2.subtitle' | transloco"
            [imageSrc]="'assets/images/topic-practice/emblem.svg'"
          ></dojo-card-link>
        </div>
      }
    </div>
  </div>
</ng-template>

@if (state.showLoadingBar) {
  <mat-progress-bar mode="indeterminate" class="page-loading-bar" />
}
<dojo-page-layout
  [wrapInsideIonContent]="false"
  [isShowBlurTopPanel]="false"
  [showHeader]="true"
  contentMaxWidth="1230px"
  class="topic-detail-view-page block h-full"
>
  @if (state.isMobile) {
    <ion-header ion-header-content [translucent]="true" class="mobile-header">
      <ion-toolbar class="!px-0">
        <div class="small:mx-auto flex items-center justify-between pr-4">
          <div
            slot="start"
            class="flex h-[52px] w-[50px] items-center justify-center"
            [routerLink]="`/${RootUrlParts.LearnHub}`"
          >
            <dojo-icon-button [icon]="'arrow-left'" [iconSize]="18" class="h-auto" />
          </div>
          <ion-title class="common-small-ion-title">{{ state.topic?.code }}</ion-title>
          <div slot="end" class="flex w-8 items-center justify-center">
            @if (state.activePage?.content?.length) {
              <dojo-icon-button [icon]="'list-tree'" [iconSize]="18" class="h-auto" (click)="toggleMobileToc()" />
            }
          </div>
        </div>
      </ion-toolbar>
    </ion-header>
  }

  <div main-content [class.mobile-toc-active]="state.isMobile">
    @if (state.topic) {
      @if (state.isMobile) {
        <!-- Mobile: always render split layout, toggle TOC visibility via CSS -->
        <div class="mobile-toc-split" [class.toc-collapsed]="!showMobileToc()">
          @if (state.activePage?.content?.length) {
            <div class="mobile-toc-area" [style.max-height]="mobileTocMaxHeight() + 'px'">
              <div class="text-secondary mb-1 text-sm font-medium">
                {{ 'learn_hub.on_this_page' | transloco }}
              </div>
              <dojo-content-block-toc [blocks]="state.activePage!.content!" [scrollableParent]="mobileTocContentEl" />
            </div>
            <div
              class="mobile-toc-gutter"
              (touchstart)="onGutterDragStart($event)"
              (mousedown)="onGutterDragStart($event)"
            >
              <div class="mobile-toc-gutter-handle"></div>
            </div>
          }
          <div #mobileTocContentEl class="mobile-toc-content">
            <ng-container *ngTemplateOutlet="mainContentTpl" />
          </div>
        </div>
      } @else {
        <!-- Desktop: standard layout -->
        <div class="main-content-header">
          <div class="back-button mb-4">
            <dojo-button
              fill="clear"
              class="text-neutral-300"
              [customWidth]="70"
              [routerLink]="`/${RootUrlParts.LearnHub}`"
            >
              <dojo-icon [icon]="'arrow-left'" class="mr-1" [size]="'18'" />
              {{ 'back' | transloco }}
            </dojo-button>
          </div>
          <dojo-topic-breadcrumbs
            class="topic-breadcrumbs"
            [activePage]="state.activePage"
            [showPageBreadcrumb]="state.showPageBreadcrumb"
          />
        </div>
        <ng-container *ngTemplateOutlet="mainContentTpl" />
      }
    }
  </div>

  <div sidebar-content class="overflow-hidden pl-[20px] pt-4">
    @if (state.activePage?.content?.length && scrollableContainer()) {
      <div class="table-contents">
        <div class="text-secondary mb-1 text-sm font-medium">
          {{ 'learn_hub.on_this_page' | transloco }}
        </div>
        <dojo-content-block-toc [blocks]="state.activePage!.content!" [scrollableParent]="scrollableContainer()" />
      </div>
    }
  </div>
</dojo-page-layout>
